import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that manages the popover + popover trigger relationship for anchoring.
 * Calculates the trigger position and inlines custom properties on the popover element
 * that can be consumed by CSS for positioning.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover – The popover element.
 * @property {HTMLElement} trigger – The popover trigger element.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <anchored-popover-component data-close-on-resize>
 *   <button data-ref="trigger" popovertarget="menu">Open Menu</button>
 *   <div data-ref="popover" id="menu" popover>Menu content</div>
 * </anchored-popover-component>
 * ```
 *
 * @property {string[]} requiredRefs - Required refs: 'popover' and 'trigger'
 * @property {number} [interaction_delay] - The delay in milliseconds for the hover interaction
 * @property {string} [data-close-on-resize] - When present, closes popover on window resize
 * @property {string} [data-hover-triggered] - When present, makes the popover function via pointerenter/leave
 * @property {number | null} [popoverTrigger] - The timeout for the popover trigger
 */
export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;
  #popoverTrigger = /** @type {number | null} */ (null);

  #shouldUseMeasuredPosition() {
    return this.hasAttribute('data-force-fixed-position');
  }

  #getPixelValue(element, propertyName, fallback = 0) {
    const value = parseFloat(getComputedStyle(element).getPropertyValue(propertyName));
    return Number.isFinite(value) ? value : fallback;
  }

  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;
    trigger.dataset.hoverActive = 'true';
    if (!popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        if (trigger.matches('[data-hover-active]')) popover.showPopover();
      }, this.interaction_delay);
    }
  };

  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;
    delete trigger.dataset.hoverActive;
    if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);
    if (popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        popover.hidePopover();
      }, this.interaction_delay);
    }
  };

  #onPopoverEnter = () => {
    if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);
  };

  #onPopoverLeave = () => {
    const { popover } = this.refs;
    this.#popoverTrigger = setTimeout(() => {
      popover.hidePopover();
    }, this.interaction_delay);
  };

  /**
   * Updates the popover position by calculating trigger element bounds
   * and setting CSS custom properties on the popover element.
   */
  #updatePosition = async () => {
    const { popover, trigger } = this.refs;
    if (!popover || !trigger) return;
    const positions = trigger.getBoundingClientRect();
    popover.style.setProperty('--anchor-top', `${positions.top}`);
    popover.style.setProperty('--anchor-right', `${window.innerWidth - positions.right}`);
    popover.style.setProperty('--anchor-bottom', `${window.innerHeight - positions.bottom}`);
    popover.style.setProperty('--anchor-left', `${positions.left}`);
    popover.style.setProperty('--anchor-height', `${positions.height}`);
    popover.style.setProperty('--anchor-width', `${positions.width}`);
  };

  /**
   * Calculates a clamped fixed position for popovers that opt out of native
   * anchor positioning so they can stay inside the viewport.
   */
  #updateMeasuredPlacement = () => {
    if (!this.#shouldUseMeasuredPosition()) return;

    const { popover, trigger } = this.refs;
    if (!popover || !trigger || !popover.matches(':popover-open')) return;

    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const viewportMargin = this.#getPixelValue(popover, '--corgi-cart-progress-popover-viewport-margin', 16);
    const spacing = this.#getPixelValue(popover, '--corgi-cart-progress-popover-spacing', 10);

    const minLeft = viewportMargin;
    const maxLeft = Math.max(minLeft, window.innerWidth - popoverRect.width - viewportMargin);
    const minTop = viewportMargin;
    const maxTop = Math.max(minTop, window.innerHeight - popoverRect.height - viewportMargin);
    const spaceBelow = window.innerHeight - triggerRect.bottom - spacing - viewportMargin;
    const spaceAbove = triggerRect.top - spacing - viewportMargin;
    const openAbove = spaceAbove > spaceBelow && spaceBelow < popoverRect.height;

    const left = Math.min(Math.max(triggerRect.left, minLeft), maxLeft);
    const preferredTop = openAbove
      ? triggerRect.top - popoverRect.height - spacing
      : triggerRect.bottom + spacing;
    const top = Math.min(Math.max(preferredTop, minTop), maxTop);

    popover.style.setProperty('--anchored-popover-fixed-left', `${left}px`);
    popover.style.setProperty('--anchored-popover-fixed-top', `${top}px`);
    popover.style.setProperty('--anchored-popover-enter-translate-y', openAbove ? '-14px' : '14px');
    popover.dataset.anchoredPlacement = openAbove ? 'top' : 'bottom';
  };

  /**
   * Debounced resize handler that optionally closes the popover
   * when the window is resized, based on the data-close-on-resize attribute.
   */
  #resizeListener = debounce(() => {
    const popover = /** @type {HTMLElement} */ (this.refs.popover);
    if (popover && popover.matches(':popover-open')) {
      if (this.#shouldUseMeasuredPosition()) {
        this.#updatePosition();
        this.#updateMeasuredPlacement();
        return;
      }
      popover.hidePopover();
    }
  }, 100);

  #beforeToggleListener = (event) => {
    const evt = /** @type {ToggleEvent} */ (event);
    const popover = /** @type {HTMLElement} */ (this.refs.popover);

    if (this.dataset.closeOnResize || this.#shouldUseMeasuredPosition()) {
      window[evt.newState === 'open' ? 'addEventListener' : 'removeEventListener']('resize', this.#resizeListener);
    }

    if (evt.newState === 'open' && (!CSS.supports('position-anchor: --trigger') || this.#shouldUseMeasuredPosition())) {
      if (this.#shouldUseMeasuredPosition()) {
        popover.style.setProperty('--anchored-popover-enter-translate-y', '14px');
      }
      this.#updatePosition();
    }
  };

  #toggleListener = (event) => {
    const evt = /** @type {ToggleEvent} */ (event);
    if (evt.newState !== 'open' || !this.#shouldUseMeasuredPosition()) return;

    requestAnimationFrame(() => {
      this.#updatePosition();
      this.#updateMeasuredPlacement();
    });
  };

  /**
   * Component initialization - sets up event listeners for resize and popover toggle events.
   */
  connectedCallback() {
    super.connectedCallback();
    const { popover, trigger } = this.refs;
    popover.addEventListener('beforetoggle', this.#beforeToggleListener);
    popover.addEventListener('toggle', this.#toggleListener);
    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter);
      trigger.addEventListener('pointerleave', this.#onTriggerLeave);
      popover.addEventListener('pointerenter', this.#onPopoverEnter);
      popover.addEventListener('pointerleave', this.#onPopoverLeave);
    }
    if (!CSS.supports('position-anchor: --trigger') || this.#shouldUseMeasuredPosition()) {
      requestIdleCallback(() => {
        this.#updatePosition();
      });
    }
  }

  /**
   * Component cleanup - removes resize event listener.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
  }
}

if (!customElements.get('anchored-popover-component')) {
  customElements.define('anchored-popover-component', AnchoredPopoverComponent);
}
